import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Tooltip,
  Alert,
  TableCell,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  RestoreFromTrash as RestoreIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Restaurant as RestaurantIcon,
  Visibility as VisibilityIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, FILE_BASE_URL } from '../config';
import { getFileUrl } from '../utils/urlHelper';
import DialogContentText from '@mui/material/DialogContentText';

interface Paciente {
  id: string;
  nombre: string;
  dni: string;
  genero: string;
  profesion: string;
  obra_social: string;
  plan: string;
  nro_socio: string;
  fecha_nacimiento: string;
  edad: number;
  altura_cm: number;
  peso_kg: number;
  religion: string;
  familia: string;
  email: string;
  email2: string;
  ruta_imagen_perfil: string;
  ultima_modificacion?: string;
  medico_id: string;
}

const PacientesEliminados: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const generoFiltro = params.get('genero');
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState<Paciente | null>(null);
  const [filtros, setFiltros] = useState({
    obraSocial: '',
    genero: '',
  });
  const [sortBy, setSortBy] = useState('nombre');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [pacienteAEliminar, setPacienteAEliminar] = useState<string | null>(null);

  useEffect(() => {
    fetchPacientesEliminados();
  }, []);

  const fetchPacientesEliminados = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/pacientes/eliminados`);
      const pacientesOrdenados = response.data.sort((a: Paciente, b: Paciente) => {
        const fechaA = a.ultima_modificacion ? new Date(a.ultima_modificacion).getTime() : 0;
        const fechaB = b.ultima_modificacion ? new Date(b.ultima_modificacion).getTime() : 0;
        return fechaB - fechaA;
      });
      setPacientes(pacientesOrdenados);
    } catch (error) {
      console.error('Error al cargar pacientes eliminados:', error);
      setError('Error al cargar pacientes eliminados');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    setFiltros({
      ...filtros,
      [event.target.name]: event.target.value,
    });
  };

  const handleSortChange = (value: string) => {
    if (sortBy === value) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(value);
      setSortDirection('asc');
    }
    handleSortClose();
  };

  const handleRestoreClick = (paciente: Paciente) => {
    setSelectedPaciente(paciente);
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (selectedPaciente) {
      try {
        await axios.put(`${API_BASE_URL}/pacientes/${selectedPaciente.id}/restore`);
        await fetchPacientesEliminados();
        setRestoreDialogOpen(false);
        setSelectedPaciente(null);
      } catch (error) {
        console.error('Error al restaurar paciente:', error);
        setError('Error al restaurar paciente');
      }
    }
  };

  const pacientesFiltrados = React.useMemo(() => {
    if (!generoFiltro) return pacientes;
    if (generoFiltro === 'SinGenero') {
      return pacientes.filter(p => !p.genero || p.genero === '');
    }
    return pacientes.filter(p => p.genero === generoFiltro);
  }, [pacientes, generoFiltro]);

  const filteredPacientes = pacientesFiltrados.filter(paciente => {
    const matchesSearch = searchTerm === '' || 
      (paciente.nombre?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (paciente.dni?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (paciente.obra_social?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (paciente.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (paciente.email2?.toLowerCase() || '').includes(searchTerm.toLowerCase());

    const matchesObraSocial = filtros.obraSocial === '' || 
      (paciente.obra_social || '') === filtros.obraSocial;

    const matchesGenero = filtros.genero === '' || 
      (paciente.genero || '') === filtros.genero;

    return matchesSearch && matchesObraSocial && matchesGenero;
  }).sort((a, b) => {
    let result = 0;
    if (sortBy === 'nombre') {
      result = (a.nombre || '').localeCompare(b.nombre || '');
    } else if (sortBy === 'edad') {
      result = (a.edad || 0) - (b.edad || 0);
    } else if (sortBy === 'obra_social') {
      result = (a.obra_social || '').localeCompare(b.obra_social || '');
    } else if (sortBy === 'ultima_modificacion') {
      const fechaA = a.ultima_modificacion ? new Date(a.ultima_modificacion).getTime() : 0;
      const fechaB = b.ultima_modificacion ? new Date(b.ultima_modificacion).getTime() : 0;
      result = fechaB - fechaA;
    }
    return sortDirection === 'asc' ? result : -result;
  });

  const obrasSociales = Array.from(new Set(pacientes.map(p => p.obra_social).filter(Boolean)));

  const handleOpenDialog = (pacienteId: string) => {
    setPacienteAEliminar(pacienteId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPacienteAEliminar(null);
  };

  const handleConfirmarEliminar = async () => {
    if (!pacienteAEliminar) return;
    try {
      await axios.delete(`${API_BASE_URL}/pacientes/${pacienteAEliminar}/definitivo`);
      setPacientes(pacientes.filter(p => p.id !== pacienteAEliminar));
      handleCloseDialog();
    } catch (error) {
      alert('Error al eliminar definitivamente el paciente.');
      handleCloseDialog();
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        p: 3, 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Cargando pacientes eliminados...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: { xs: '100vw', sm: 'none' }, overflowX: { xs: 'hidden', sm: 'visible' } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Pacientes Eliminados
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {filteredPacientes.length} paciente{filteredPacientes.length !== 1 ? 's' : ''} eliminado{filteredPacientes.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => navigate('/pacientes')}
          sx={{ borderRadius: 2 }}
        >
          Volver a Pacientes
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {pacientes.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No hay pacientes eliminados para mostrar.
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="Buscar pacientes..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={8}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Obra Social</InputLabel>
                <Select
                  value={filtros.obraSocial}
                  name="obraSocial"
                  onChange={handleFilterChange}
                  label="Obra Social"
                >
                  <MenuItem value="">Todas</MenuItem>
                  {obrasSociales.map((obra) => (
                    <MenuItem key={obra} value={obra}>
                      {obra}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Género</InputLabel>
                <Select
                  value={filtros.genero}
                  name="genero"
                  onChange={handleFilterChange}
                  label="Género"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Masculino">Masculino</MenuItem>
                  <MenuItem value="Femenino">Femenino</MenuItem>
                  <MenuItem value="SinGenero">Sin Género</MenuItem>
                </Select>
              </FormControl>
              <IconButton onClick={handleSortClick}>
                <SortIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortClose}
      >
        <MenuItem onClick={() => handleSortChange('nombre')}>
          Nombre {sortBy === 'nombre' && (sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />)}
        </MenuItem>
        <MenuItem onClick={() => handleSortChange('edad')}>
          Edad {sortBy === 'edad' && (sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />)}
        </MenuItem>
        <MenuItem onClick={() => handleSortChange('obra_social')}>
          Obra Social {sortBy === 'obra_social' && (sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />)}
        </MenuItem>
        <MenuItem onClick={() => handleSortChange('ultima_modificacion')}>
          Última Modificación {sortBy === 'ultima_modificacion' && (sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />)}
        </MenuItem>
      </Menu>

      <Grid container spacing={3}>
        {filteredPacientes.map((paciente) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={paciente.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                border: '2px solid',
                borderColor: 'warning.main',
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    src={getFileUrl(paciente.ruta_imagen_perfil)}
                    sx={{ width: 50, height: 50, mr: 2 }}
                  >
                    {paciente.nombre?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h2" noWrap>
                      {paciente.nombre}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {paciente.dni}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Edad:</strong> {paciente.edad} años
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Obra Social:</strong> {paciente.obra_social || 'No especificada'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Email:</strong> {paciente.email || 'No especificado'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="ELIMINADO"
                    color="warning"
                    size="small"
                    icon={<WarningIcon />}
                  />
                  {paciente.genero && (
                    <Chip
                      label={paciente.genero}
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
              </CardContent>

              <Box sx={{ p: 2, pt: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Tooltip title="Restaurar Paciente">
                    <IconButton
                      onClick={() => handleRestoreClick(paciente)}
                      color="primary"
                      size="small"
                    >
                      <RestoreIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar definitivamente">
                    <IconButton
                      onClick={() => handleOpenDialog(paciente.id)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
        <DialogTitle>Restaurar Paciente</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que quieres restaurar al paciente "{selectedPaciente?.nombre}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            El paciente volverá a aparecer en la lista principal de pacientes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleRestoreConfirm} color="primary" variant="contained">
            Restaurar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="dialog-eliminar-title"
      >
        <DialogTitle id="dialog-eliminar-title" sx={{ color: 'error.main', fontWeight: 700 }}>
          Eliminar paciente definitivamente
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que quieres eliminar este paciente de forma <b>definitiva</b>?<br/>
            <span style={{ color: '#d32f2f', fontWeight: 500 }}>Esta acción no se puede deshacer.</span>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleConfirmarEliminar} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PacientesEliminados; 